import { memo, useCallback } from 'react';

import { Check, Close } from '@air/next-icons';
import { Chip } from '@air/primitive-chip';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputTrigger,
  type ComboboxInputTriggerProps,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
} from '@air/primitive-combobox';
import { IconButton } from '@air/primitive-icon-button';

import { type CustomFieldValueFilterBase } from './utils';

export type CustomFieldValuesFilterSelectProps = {
  isLoading?: boolean;
  onSelectedValuesChanged: (values: CustomFieldValueFilterBase[]) => void;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  selectedValues: CustomFieldValueFilterBase[];
  values: CustomFieldValueFilterBase[];
} & Pick<ComboboxInputTriggerProps, 'className'>;

export const CustomFieldValuesFilterSelect = memo(
  ({
    isLoading,
    onSelectedValuesChanged,
    onInputChange,
    placeholder = 'Select field values',
    selectedValues,
    values,
    className,
  }: CustomFieldValuesFilterSelectProps) => {
    const onRemoveValue = useCallback(
      (value: CustomFieldValueFilterBase) => {
        onSelectedValuesChanged(selectedValues.filter((v) => v.id !== value.id));
      },
      [onSelectedValuesChanged, selectedValues],
    );

    const onValueClick = useCallback(
      (option: CustomFieldValueFilterBase) => {
        if (selectedValues.some((v) => v.id === option.id)) {
          onRemoveValue(option);
        } else {
          onSelectedValuesChanged([...selectedValues, option]);
        }
      },
      [onRemoveValue, onSelectedValuesChanged, selectedValues],
    );

    return (
      <Combobox>
        <ComboboxInputTrigger className={className} size="large">
          {selectedValues.length > 0 ? (
            selectedValues.map((value) => (
              <Chip key={value.id} shape="rounded" size="medium">
                <span className="truncate">{value.value}</span>
                <IconButton
                  appearance="ghost"
                  color="grey"
                  icon={Close}
                  label="Remove custom field"
                  onClick={(event) => {
                    event.stopPropagation();

                    onRemoveValue(value);
                  }}
                  size="extra-small"
                />
              </Chip>
            ))
          ) : (
            <div className="text-grey-10">{placeholder}</div>
          )}
        </ComboboxInputTrigger>
        <ComboboxContent
          className="max-h-[min(320px,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)]"
          sideOffset={4}
        >
          <ComboboxInput onValueChange={onInputChange} placeholder="Search custom field values..." />

          {isLoading ? (
            <ComboboxLoading>Loading...</ComboboxLoading>
          ) : (
            <ComboboxEmpty>No custom field found.</ComboboxEmpty>
          )}

          <ComboboxList>
            {values.map((value) => {
              const isSelected = selectedValues.some((v) => v.id === value.id);

              return (
                <ComboboxItem
                  className="justify-between"
                  data-id={value.id}
                  onSelect={() => onValueClick(value)}
                  key={value.id}
                >
                  <Chip
                    className="truncate"
                    shape="rectangular"
                    size="medium"
                    style={{
                      backgroundColor: value.color.backgroundHex,
                      color: value.color.fontHex,
                    }}
                  >
                    <span className="truncate">{value.value}</span>
                  </Chip>
                  {isSelected ? <Check className="size-4 shrink-0 text-blue-9" /> : <div className="size-4 shrink-0" />}
                </ComboboxItem>
              );
            })}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  },
);

CustomFieldValuesFilterSelect.displayName = 'CustomFieldValuesFilterSelect';
